import { Layers } from "lucide-react";
import useIsNarrow from "@/hooks/useIsNarrow";

const fmt = (n, d = 2) =>
  (n === null || n === undefined || isNaN(n)) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

export const OPT_PNL_TITLE =
  "Option bracket: its stop/target are underlying levels, its entry is the option premium. "
  + "See the position / order P&L.";
export const LOT_UNKNOWN_TITLE = "Lot size unknown for this older bracket: P&L not shown. See the position / order P&L.";
export const LEGACY_TITLE = "Older bracket: computed from the trigger level × lot, not the booked fill.";
export const BOOKED_TITLE = "Booked P&L of the bracket's exit (close fill vs entry × lot).";

// RU-MOBILECARDS MC-12: the P&L the monitor stored when the exit was booked;
// rows closed before that: never for an option (its exit_price is the
// underlying level, its entry the premium: the FINNIFTY "+₹24,497"), and
// for EQ/FUT the old formula only with a known lot. Returns { pnl, title }.
export function bracketPnl(b) {
  if (b.pnl_source === "booked" && b.pnl !== null && b.pnl !== undefined && !isNaN(b.pnl)) {
    return { pnl: Number(b.pnl).toFixed(2), title: BOOKED_TITLE };
  }
  const it = String(b.instrument_type || "EQ").toUpperCase();
  if (it === "OPT") return { pnl: null, title: OPT_PNL_TITLE };
  if (!(b.exit_price && b.entry_price)) return { pnl: null, title: undefined };
  const stored = typeof b.lot_size === "number" && b.lot_size > 0 ? b.lot_size : null;
  const lot = b.lot_unknown ? null : stored ?? (it === "EQ" ? 1 : null);
  if (lot === null) return { pnl: null, title: LOT_UNKNOWN_TITLE };
  return {
    pnl: ((b.exit_price - b.entry_price) * (b.side === "BUY" ? 1 : -1) * b.qty * lot).toFixed(2),
    title: LEGACY_TITLE,
  };
}

function statusClass(b) {
  const isActive = b.status === "ACTIVE";
  const isHit = b.status === "TARGET_HIT";
  const isStopped = b.status === "STOPPED";
  return isActive ? "border-blue-500/40 text-blue-400" :
    isHit ? "border-emerald-500/40 txt-profit" :
    isStopped ? "border-red-500/40 txt-loss" : "border-[var(--border)] txt-muted";
}

const pnlCls = (pnl) => (pnl > 0 ? "txt-profit" : pnl < 0 ? "txt-loss" : "txt-muted");

function CardField({ label, children, className = "", title, testid }) {
  return (
    <div className="min-w-0" title={title} data-testid={testid}>
      <div className="overline text-[10px] txt-muted">{label}</div>
      <div className={`font-mono-data text-xs break-words ${className}`}>{children}</div>
    </div>
  );
}

export default function BracketOrdersTable({ brackets }) {
  const narrow = useIsNarrow();
  return (
    <div className="panel">
      <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
        <Layers className="w-4 h-4 txt-secondary" />
        <span className="overline">Bracket Orders ({brackets.length})</span>
      </div>
      {narrow ? (
        <div className="p-3 space-y-2" data-testid="brackets-cards">
          {brackets.map((b) => {
            const isActive = b.status === "ACTIVE";
            const { pnl, title: pnlTitle } = bracketPnl(b);
            return (
              <div key={b.id} data-testid={`bracket-card-${b.id}`}
                   className={`border border-[var(--border)] p-3 ${isActive ? "" : "opacity-50"}`}>
                <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                  <span className="font-section font-bold break-words">{b.symbol}</span>
                  <span className={`font-section text-[10px] px-1.5 py-0.5 border border-[var(--border)] ${b.side === "BUY" ? "txt-profit" : "txt-loss"}`}
                        data-testid={`bracket-card-side-${b.id}`}>{b.side}</span>
                  <span className={`font-section text-[10px] px-1.5 py-0.5 border ${statusClass(b)}`}
                        data-testid={`bracket-card-status-${b.id}`}>{b.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-2">
                  <CardField label="Qty" testid={`bracket-card-qty-${b.id}`}>{b.qty}</CardField>
                  <CardField label="P&L" className={pnlCls(pnl)} testid={`bracket-card-pnl-${b.id}`}
                             title={pnlTitle}>
                    {pnl !== null ? `₹${pnl}` : "—"}
                  </CardField>
                  <CardField label="SL" className="txt-loss" testid={`bracket-card-sl-${b.id}`}>₹{fmt(b.stop_loss)}</CardField>
                  <CardField label="Target" className="txt-profit" testid={`bracket-card-target-${b.id}`}>₹{fmt(b.target_price)}</CardField>
                  <CardField label="Trail" className="txt-muted" testid={`bracket-card-trail-${b.id}`}>
                    {b.trail_percent ? `${b.trail_percent}%` : "—"}
                  </CardField>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono-data">
          <thead>
            <tr className="border-b border-[var(--border)]">
              {["Symbol","Side","Qty","SL","Target","Trail%","Status","P&L"].map((h) => (
                <th key={h} className="overline px-3 py-2 text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {brackets.map((b) => {
              const isActive = b.status === "ACTIVE";
              const { pnl, title: pnlTitle } = bracketPnl(b);
              return (
                <tr key={b.id} className={`border-b border-[var(--border)] ${isActive ? "" : "opacity-50"}`}>
                  <td className="px-3 py-2 font-section font-bold">{b.symbol}</td>
                  <td className={`px-3 py-2 font-section ${b.side === "BUY" ? "txt-profit" : "txt-loss"}`}>{b.side}</td>
                  <td className="px-3 py-2">{b.qty}</td>
                  <td className="px-3 py-2 txt-loss">₹{fmt(b.stop_loss)}</td>
                  <td className="px-3 py-2 txt-profit">₹{fmt(b.target_price)}</td>
                  <td className="px-3 py-2 txt-muted">{b.trail_percent ? `${b.trail_percent}%` : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`font-section text-xs px-2 py-0.5 border ${statusClass(b)}`}>
                      {b.status}
                    </span>
                  </td>
                  <td className={`px-3 py-2 ${pnlCls(pnl)}`}
                      title={pnlTitle}>
                    {pnl !== null ? `₹${pnl}` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
